import { useEffect, useRef, useState, type DragEvent, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Download,
  FileArchive,
  FileText,
  Link2,
  Plus,
  Upload,
  X,
} from 'lucide-react';
import {
  Chargement,
  Compteur,
  EcranEnErreur,
  EnteteEcran,
  Pastille,
} from '../components/ui/Primitives';
import { useToast } from '../components/ui/useToast';
import {
  urlFichierDepotDevoir,
  useDeposerDevoir,
  useDevoirsApprenant,
  type DepotDevoirApprenant,
  type DevoirApprenant,
  type LimitesDepotDevoir,
  type TypeDevoir,
} from '../lib/useEspaceApprenant';
import { tailleRessource } from '../lib/useLecteur';
import { ErreurApi } from '../lib/api';
import { LIBELLES_STATUT_DEVOIR, echeanceLongue, noteSur20, pastilleDevoir } from '../lib/affichage-devoirs';
import { attributsOnglet, attributsPanneau, clavierOnglets } from '../components/ui/onglets-aria';

/**
 * « Devoirs & évaluations » (écran `assignments` de la maquette validée).
 *
 * Tout vient de `/espace-apprenant/devoirs` : les devoirs demandés au dossier,
 * leur statut — à rendre, rendu, corrigé — et leur retard, les dépôts de
 * l'apprenant et ce qui en refuse un nouveau. L'écran ne juge rien : il
 * contrôle le format et le poids avant l'envoi pour éviter un envoi inutile,
 * l'API tranche (TF-453). Un dépôt joint un fichier PDF ou ZIP et/ou des
 * liens ; le suivant remplace le précédent pour la correction, sans l'effacer.
 *
 * La note et le commentaire n'apparaissent qu'une fois la correction publiée
 * par le formateur (TF-457) ; la moyenne ne compte que ces notes-là. Un quiz
 * fini montre d'ici là sa note automatique, dite provisoire. Pas de critères
 * de notation, d'anti-plagiat ni d'export, absents de la base.
 *
 * `?devoir=` ouvre un devoir déplié, sous l'onglet qui le montre : c'est par
 * là que le tableau de bord y mène.
 */

const LIBELLES_TYPE: Record<TypeDevoir, string> = {
  PROJET: 'Projet',
  QUIZ: 'Quiz',
  RAPPORT: 'Rapport',
  AUTRE: 'Devoir',
};

type Onglet = 'arendre' | 'rendus' | 'corriges' | 'tous';

const ONGLET_DU_STATUT: Record<DevoirApprenant['statut'], Onglet> = {
  A_RENDRE: 'arendre',
  RENDU: 'rendus',
  CORRIGE: 'corriges',
};

const TEINTES_ICONE: Record<string, string> = {
  danger: 'bg-red-100 text-red-600',
  warning: 'bg-amber-100 text-amber-600',
  info: 'bg-blue-100 text-codialis-sky',
  success: 'bg-codialis-mint/20 text-codialis-teal',
};

const CLASSE_CHAMP =
  'w-full min-w-0 rounded-lg border border-gray-200 px-3 py-2 text-sm text-codialis-navy focus:outline-none focus:ring-2 focus:ring-codialis-mint [@media(pointer:coarse)]:min-h-[40px]';

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

function IconeFichier({ typeMime }: { typeMime: string }): JSX.Element {
  const Icone = typeMime === 'application/zip' ? FileArchive : FileText;
  return <Icone className="h-4 w-4 shrink-0 text-codialis-gray" aria-hidden />;
}

function LigneDepot({
  depot,
  retenu,
  corrige = false,
}: {
  depot: DepotDevoirApprenant;
  retenu: boolean;
  corrige?: boolean;
}): JSX.Element {
  return (
    <li className={`rounded-xl border p-3 ${retenu ? 'border-codialis-mint/40 bg-white' : 'border-gray-100 bg-gray-50'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-codialis-navy">Dépôt n°{depot.numero}</span>
        <span className="text-xs text-codialis-gray">{echeanceLongue(depot.deposeLe)}</span>
        {depot.enRetard && <Pastille texte="En retard" ton="warning" />}
        {retenu ? (
          <Pastille texte={corrige ? 'Corrigé' : 'Retenu pour la correction'} ton="mint" />
        ) : (
          <Pastille texte="Remplacé" ton="neutral" />
        )}
      </div>
      <ul className="mt-2 space-y-1.5">
        {depot.fichier && (
          <li className="flex min-w-0 items-center gap-2 text-sm">
            <IconeFichier typeMime={depot.fichier.typeMime} />
            <a
              href={urlFichierDepotDevoir(depot.id)}
              className="min-w-0 truncate font-medium text-codialis-sky hover:underline"
              download
            >
              {depot.fichier.nom}
            </a>
            <span className="shrink-0 text-xs text-codialis-gray">{tailleRessource(depot.fichier.tailleOctets)}</span>
            <Download className="h-3.5 w-3.5 shrink-0 text-codialis-gray" aria-hidden />
          </li>
        )}
        {depot.liens.map((lien) => (
          <li key={lien} className="flex min-w-0 items-center gap-2 text-sm">
            <Link2 className="h-4 w-4 shrink-0 text-codialis-gray" aria-hidden />
            <a
              href={lien}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 truncate font-medium text-codialis-sky hover:underline"
            >
              {lien}
            </a>
          </li>
        ))}
      </ul>
    </li>
  );
}

/**
 * Rendre le devoir : un fichier déposé ou choisi, et/ou des liens. Le format
 * et le poids sont contrôlés avant l'envoi ; l'API juge.
 */
function FormulaireDepot({
  devoir,
  limites,
  onRendu,
}: {
  devoir: DevoirApprenant;
  limites: LimitesDepotDevoir;
  onRendu: () => void;
}): JSX.Element {
  const toast = useToast();
  const deposer = useDeposerDevoir();
  const choixFichier = useRef<HTMLInputElement>(null);
  const [fichier, setFichier] = useState<File | null>(null);
  const [liens, setLiens] = useState<string[]>(['']);
  const [survol, setSurvol] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const precedent = devoir.depots[0] ?? null;
  const saisis = liens.map((lien) => lien.trim()).filter(Boolean);

  const choisir = (choisi: File) => {
    setErreur(null);
    const point = choisi.name.lastIndexOf('.');
    const extension = point >= 0 ? choisi.name.slice(point).toLowerCase() : '';
    if (!limites.extensions.includes(extension)) {
      setErreur(`« ${choisi.name} » n’est pas accepté. Formats acceptés : ${limites.libelleFormats}.`);
      return;
    }
    if (choisi.size > limites.tailleMaxOctets) {
      setErreur(`« ${choisi.name} » est trop volumineux : ${limites.libelleTailleMax} au plus.`);
      return;
    }
    setFichier(choisi);
  };

  const deposerFichier = (evenement: DragEvent<HTMLDivElement>) => {
    evenement.preventDefault();
    setSurvol(false);
    const depose = evenement.dataTransfer.files[0];
    if (depose) choisir(depose);
  };

  const envoyer = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    try {
      const reponse = await deposer.mutateAsync({ devoirId: devoir.id, fichier, liens: saisis });
      const rendu = reponse.tous.find((un) => un.id === devoir.id);
      toast(rendu?.depots[0]?.enRetard ? 'Devoir rendu · signalé en retard à votre formateur' : 'Devoir rendu · votre formateur est prévenu');
      setFichier(null);
      setLiens(['']);
      onRendu();
    } catch (cause) {
      setErreur(messageErreur(cause, 'Votre travail n’a pas pu être déposé.'));
    }
  };

  return (
    // `noValidate` : « github.com/… » sans https:// est accepté, l'API le complète.
    <form onSubmit={envoyer} noValidate className="space-y-3">
      <input
        ref={choixFichier}
        type="file"
        accept={[...limites.extensions, 'application/pdf', 'application/zip'].join(',')}
        className="hidden"
        aria-label="Choisir un fichier"
        onChange={(evenement) => {
          const choisi = evenement.target.files?.[0];
          evenement.target.value = '';
          if (choisi) choisir(choisi);
        }}
      />
      {fichier ? (
        <div className="flex min-w-0 items-center gap-3 rounded-xl border border-codialis-mint/40 bg-white p-3">
          <IconeFichier typeMime={fichier.name.toLowerCase().endsWith('.zip') ? 'application/zip' : 'application/pdf'} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-codialis-navy">{fichier.name}</div>
            <div className="text-xs text-codialis-gray">{tailleRessource(fichier.size)}</div>
          </div>
          <button
            type="button"
            onClick={() => setFichier(null)}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-codialis-gray hover:bg-gray-100 hover:text-codialis-navy [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10"
            aria-label={`Retirer ${fichier.name}`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(evenement) => {
            evenement.preventDefault();
            setSurvol(true);
          }}
          onDragLeave={() => setSurvol(false)}
          onDrop={deposerFichier}
          className={`rounded-xl border-2 border-dashed p-6 text-center transition sm:p-8 ${survol ? 'border-codialis-mint bg-codialis-mint/5' : 'border-gray-200 hover:border-codialis-mint'}`}
        >
          <Upload className="mx-auto mb-2 h-10 w-10 text-codialis-gray" aria-hidden />
          <p className="font-medium text-codialis-navy">Déposez votre fichier ici</p>
          <p className="mt-1 text-xs text-codialis-gray">
            ou{' '}
            <button
              type="button"
              onClick={() => choixFichier.current?.click()}
              className="font-semibold text-codialis-sky hover:underline"
            >
              parcourir
            </button>{' '}
            · {limites.libelleFormats}, {limites.libelleTailleMax} au plus · plusieurs fichiers : en ZIP
          </p>
        </div>
      )}

      <div className="space-y-2">
        <label htmlFor={`lien-${devoir.id}-0`} className="block text-xs font-semibold uppercase text-codialis-navy">
          Liens (GitHub, déploiement…) · {limites.liensMax} au plus
        </label>
        {liens.map((lien, rang) => (
          <div key={rang} className="flex min-w-0 gap-2">
            <input
              id={`lien-${devoir.id}-${rang}`}
              type="url"
              inputMode="url"
              value={lien}
              onChange={(evenement) => setLiens(liens.map((autre, i) => (i === rang ? evenement.target.value : autre)))}
              placeholder="https://github.com/votre-depot"
              aria-label={`Lien ${rang + 1}`}
              className={`flex-1 ${CLASSE_CHAMP}`}
            />
            {liens.length > 1 && (
              <button
                type="button"
                onClick={() => setLiens(liens.filter((_, i) => i !== rang))}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-codialis-gray hover:bg-gray-100 hover:text-codialis-navy [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10"
                aria-label={`Retirer le lien ${rang + 1}`}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
        {liens.length < limites.liensMax && (
          <button
            type="button"
            onClick={() => setLiens([...liens, ''])}
            className="inline-flex items-center gap-1 text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
          >
            <Plus className="h-3.5 w-3.5" /> Ajouter un lien
          </button>
        )}
      </div>

      {devoir.enRetard && (
        <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
          L’échéance est passée : votre dépôt sera signalé en retard à votre formateur.
        </p>
      )}
      {precedent && (
        <p className="text-xs text-codialis-gray">
          Ce dépôt remplacera le n°{precedent.numero} pour la correction ({devoir.depots.length + 1} sur {limites.depotsMax}).
        </p>
      )}
      {erreur && (
        <p role="alert" className="text-sm text-red-600">
          {erreur}
        </p>
      )}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={deposer.isPending || (!fichier && saisis.length === 0)}
          className="rounded-lg bg-codialis-mint px-5 py-2 text-sm font-semibold text-codialis-navy transition hover:bg-codialis-teal disabled:cursor-not-allowed disabled:opacity-50 [@media(pointer:coarse)]:min-h-[40px]"
        >
          {deposer.isPending ? 'Envoi…' : precedent ? 'Déposer une nouvelle version' : 'Rendre le devoir'}
        </button>
      </div>
    </form>
  );
}

/** La correction publiée par le formateur : sa note et son commentaire. */
function BlocCorrection({ correction }: { correction: NonNullable<DevoirApprenant['correction']> }): JSX.Element {
  return (
    <section className="rounded-xl border border-codialis-mint/30 bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <CheckCircle2 className="h-5 w-5 text-codialis-teal" aria-hidden />
        <h4 className="font-display font-semibold text-codialis-navy">Correction</h4>
      </div>
      <div className="font-display mt-2 text-4xl font-extrabold text-codialis-navy">
        {String(correction.note).replace('.', ',')}
        <span className="text-base text-codialis-gray">/20</span>
      </div>
      {correction.commentaire ? (
        <p className="mt-3 whitespace-pre-line break-words text-sm text-codialis-navy">{correction.commentaire}</p>
      ) : (
        <p className="mt-3 text-sm text-codialis-gray">Pas de commentaire.</p>
      )}
      <p className="mt-3 text-xs text-codialis-gray">
        Corrigé{correction.correcteur ? ` par ${correction.correcteur}` : ''} le {echeanceLongue(correction.publieeLe).toLowerCase()}
        {correction.depot !== null ? ` · dépôt n°${correction.depot}` : ''}
        {correction.rectifieeLe ? ` · note modifiée le ${echeanceLongue(correction.rectifieeLe).toLowerCase()}` : ''}
      </p>
    </section>
  );
}

function DetailDevoir({
  devoir,
  limites,
  onRendu,
}: {
  devoir: DevoirApprenant;
  limites: LimitesDepotDevoir;
  onRendu: () => void;
}): JSX.Element {
  const naviguer = useNavigate();
  const pastille = pastilleDevoir(devoir);
  // La pastille de la ligne dit le temps qui reste ; ici, le statut.
  const statut = devoir.statut === 'A_RENDRE' && !devoir.enRetard ? LIBELLES_STATUT_DEVOIR.A_RENDRE : pastille.texte;
  const estQuiz = devoir.type === 'QUIZ';

  return (
    <div className="grid grid-cols-1 gap-5 border-t border-gray-100 bg-gray-50 px-4 py-4 sm:px-6 sm:py-5 lg:grid-cols-3">
      <div className="min-w-0 space-y-5 lg:col-span-2">
        <section>
          <h4 className="font-display mb-2 font-semibold text-codialis-navy">Consignes</h4>
          <p className="whitespace-pre-line text-sm text-codialis-gray">
            {devoir.description ?? 'Aucune consigne détaillée n’a été renseignée pour ce devoir.'}
          </p>
        </section>

        {devoir.correction && <BlocCorrection correction={devoir.correction} />}

        {estQuiz ? (
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => naviguer(`/quiz/${devoir.id}`)}
              className="flex min-h-[44px] items-center gap-2 rounded-xl bg-codialis-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-codialis-deep"
            >
              {devoir.statut !== 'A_RENDRE' ? 'Voir mon résultat' : devoir.quiz?.commence ? 'Reprendre le quiz' : 'Faire le quiz'}
              <ChevronRight className="h-4 w-4" />
            </button>
            {devoir.quiz && !devoir.correction && (
              <span className="text-sm text-codialis-gray">
                {devoir.quiz.note !== null
                  ? `Note automatique : ${noteSur20(devoir.quiz.note)}, provisoire jusqu’à la validation de votre formateur`
                  : `${devoir.quiz.questions} question${devoir.quiz.questions > 1 ? 's' : ''} · ${devoir.quiz.dureeMinutes} min`}
              </span>
            )}
          </div>
        ) : (
          <>
            {devoir.depots.length > 0 && (
              <section>
                <h4 className="font-display mb-2 font-semibold text-codialis-navy">
                  {devoir.depots.length > 1 ? 'Mes dépôts' : 'Mon dépôt'}
                </h4>
                <ul className="space-y-2">
                  {devoir.depots.map((depot, rang) => (
                    <LigneDepot
                      key={depot.id}
                      depot={depot}
                      retenu={devoir.correction?.depot != null ? depot.numero === devoir.correction.depot : rang === 0}
                      corrige={devoir.correction !== null}
                    />
                  ))}
                </ul>
              </section>
            )}
            <section>
              <h4 className="font-display mb-2 font-semibold text-codialis-navy">
                {devoir.depots.length > 0 ? 'Déposer une nouvelle version' : 'Rendre le devoir'}
              </h4>
              {devoir.refusDepot ? (
                <p className="rounded-lg bg-white px-3 py-2 text-sm text-codialis-gray">{devoir.refusDepot}</p>
              ) : (
                <FormulaireDepot devoir={devoir} limites={limites} onRendu={onRendu} />
              )}
            </section>
          </>
        )}
      </div>

      <aside className="min-w-0">
        <div className="rounded-xl border border-codialis-mint/20 bg-gradient-to-br from-codialis-mint/10 to-codialis-sky/10 p-4">
          <h4 className="mb-3 text-sm font-semibold text-codialis-navy">Informations</h4>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-codialis-gray">Formateur</dt>
              <dd className="text-right font-medium text-codialis-navy">{devoir.formateur ?? 'Non désigné'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="shrink-0 text-codialis-gray">Date limite</dt>
              <dd className="text-right font-medium text-codialis-navy">{echeanceLongue(devoir.echeance)}</dd>
            </div>
            {!estQuiz && (
              <div className="flex justify-between gap-3">
                <dt className="text-codialis-gray">Dépôts</dt>
                <dd className="font-medium text-codialis-navy">
                  {devoir.depots.length} sur {limites.depotsMax}
                </dd>
              </div>
            )}
            <div className="flex items-center justify-between gap-3">
              <dt className="text-codialis-gray">Statut</dt>
              <dd>
                <Pastille texte={statut} ton={pastille.ton} />
              </dd>
            </div>
          </dl>
        </div>
      </aside>
    </div>
  );
}

function LigneDevoir({
  devoir,
  limites,
  ouvert,
  onBasculer,
  onRendu,
}: {
  devoir: DevoirApprenant;
  limites: LimitesDepotDevoir;
  ouvert: boolean;
  onBasculer: () => void;
  onRendu: () => void;
}): JSX.Element {
  const pastille = pastilleDevoir(devoir);
  const dernier = devoir.depots[0];
  const etat =
    devoir.statut === 'A_RENDRE'
      ? devoir.type === 'QUIZ'
        ? 'Quiz à faire'
        : 'Non rendu'
      : devoir.correction
        ? `Corrigé le ${echeanceLongue(devoir.correction.publieeLe).toLowerCase()}`
        : dernier
          ? `Rendu le ${echeanceLongue(dernier.deposeLe).toLowerCase()}`
          : LIBELLES_STATUT_DEVOIR[devoir.statut];
  return (
    <div id={`devoir-${devoir.id}`}>
      <button
        type="button"
        onClick={onBasculer}
        aria-expanded={ouvert}
        className="flex w-full items-center gap-3 px-4 py-4 text-left transition hover:bg-gray-50 sm:gap-4 sm:px-6"
      >
        <div className={`hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg sm:flex ${TEINTES_ICONE[pastille.ton] ?? TEINTES_ICONE.info}`}>
          <ClipboardList className="h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm font-semibold text-codialis-navy">{devoir.titre}</div>
            <Pastille texte={LIBELLES_TYPE[devoir.type]} ton="neutral" />
          </div>
          {/* Au téléphone, l'intitulé du bloc tient en deux lignes ; il se lit en entier une fois le devoir ouvert. */}
          <div className="line-clamp-2 text-xs text-codialis-gray sm:line-clamp-none">
            {devoir.cours}
            {devoir.formateur ? ` · ${devoir.formateur}` : ''}
          </div>
        </div>
        <div className="hidden text-right sm:block">
          <div className="text-sm font-medium text-codialis-navy">{echeanceLongue(devoir.echeance)}</div>
          <div className="text-xs text-codialis-gray">{etat}</div>
        </div>
        <Pastille texte={pastille.texte} ton={pastille.ton} />
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-codialis-gray transition-transform ${ouvert ? 'rotate-90' : ''}`}
          aria-hidden
        />
      </button>
      {ouvert && (
        <>
          <div className="space-y-0.5 px-4 pb-3 text-xs text-codialis-gray sm:hidden">
            <div>{devoir.cours}</div>
            <div>
              Échéance : {echeanceLongue(devoir.echeance)} · {etat}
            </div>
          </div>
          <DetailDevoir devoir={devoir} limites={limites} onRendu={onRendu} />
        </>
      )}
    </div>
  );
}

export function DevoirsPage(): JSX.Element {
  const { data, isLoading, error } = useDevoirsApprenant();
  const [parametres] = useSearchParams();
  const demande = parametres.get('devoir');
  const [ongletChoisi, setOnglet] = useState<Onglet | null>(null);
  const [devoirOuvert, setDevoirOuvert] = useState<string | null>(demande);
  const [aMontrer, setAMontrer] = useState<string | null>(demande);

  // Le devoir demandé se montre dans la page, une fois la liste arrivée.
  useEffect(() => {
    if (aMontrer && data) document.getElementById(`devoir-${aMontrer}`)?.scrollIntoView?.({ block: 'center' });
  }, [aMontrer, data, ongletChoisi]);

  if (isLoading) return <Chargement libelle="Chargement de vos devoirs…" />;
  if (error) {
    return (
      <EcranEnErreur
        titre="Devoirs à rendre"
        message={error instanceof ErreurApi ? error.message : 'Vos devoirs n’ont pas pu être chargés.'}
      />
    );
  }
  if (!data) return <EcranEnErreur titre="Devoirs à rendre" message="Vos devoirs n’ont pas pu être chargés." />;

  const rendus = data.tous.filter((devoir) => devoir.statut === 'RENDU');
  const corriges = data.tous.filter((devoir) => devoir.statut === 'CORRIGE');
  const enRetard = data.aRendre.filter((devoir) => devoir.enRetard).length;
  const listes: Record<Onglet, DevoirApprenant[]> = { arendre: data.aRendre, rendus, corriges, tous: data.tous };
  const devoirDemande = demande ? data.tous.find((devoir) => devoir.id === demande) : undefined;
  const onglet = ongletChoisi ?? (devoirDemande ? ONGLET_DU_STATUT[devoirDemande.statut] : 'arendre');
  const liste = listes[onglet];
  const aDeposer = data.aRendre.find((devoir) => devoir.refusDepot === null);

  const ouvrir = (devoirId: string, vers: Onglet) => {
    setOnglet(vers);
    setDevoirOuvert(devoirId);
    setAMontrer(devoirId);
  };

  const ONGLETS: { cle: Onglet; libelle: string; compte?: number }[] = [
    { cle: 'arendre', libelle: 'À rendre', compte: data.aRendre.length },
    { cle: 'rendus', libelle: 'Rendus' },
    { cle: 'corriges', libelle: 'Corrigés' },
    { cle: 'tous', libelle: 'Tous' },
  ];
  const VIDES: Record<Onglet, string> = {
    arendre: 'Aucun devoir à rendre pour le moment.',
    rendus: 'Aucun devoir rendu en attente de correction.',
    corriges: 'Aucune correction publiée pour le moment.',
    tous: 'Aucun devoir n’a encore été publié sur votre formation.',
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Devoirs & évaluations"
        sousTitre="Vos travaux à rendre, vos dépôts et leur correction"
        actions={
          aDeposer && (
            <button
              type="button"
              onClick={() => ouvrir(aDeposer.id, 'arendre')}
              className="flex items-center gap-2 rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
            >
              <Upload className="h-4 w-4" aria-hidden />
              Déposer un travail
            </button>
          )
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Compteur
          libelle="À rendre"
          valeur={data.aRendre.length}
          detail={enRetard > 0 ? `dont ${enRetard} en retard` : 'aucun en retard'}
          ton="red"
        />
        <Compteur libelle="Rendus" valeur={rendus.length} detail="en attente de correction" ton="blue" />
        <Compteur
          libelle="Corrigés"
          valeur={corriges.length}
          detail={corriges.length > 1 ? 'corrections publiées' : 'correction publiée'}
          ton="mint"
        />
        <Compteur
          libelle="Moyenne"
          valeur={data.moyenne === null ? '—' : noteSur20(data.moyenne)}
          detail={data.moyenne === null ? 'aucune note publiée' : `sur ${corriges.length} correction${corriges.length > 1 ? 's' : ''} publiée${corriges.length > 1 ? 's' : ''}`}
          ton="warm"
        />
      </div>

      <div className="overflow-hidden rounded-2xl bg-white shadow-card">
        <div
          role="tablist"
          aria-label="Filtrer les devoirs"
          onKeyDown={clavierOnglets(
            ONGLETS.map((entree) => entree.cle),
            onglet,
            setOnglet,
          )}
          className="flex gap-6 overflow-x-auto border-b border-gray-100 px-4 sm:px-6"
        >
          {ONGLETS.map(({ cle, libelle, compte }) => (
            <button
              key={cle}
              type="button"
              {...attributsOnglet(cle, onglet === cle, 'panneau-devoirs')}
              onClick={() => setOnglet(cle)}
              className={`shrink-0 border-b-2 py-4 text-sm ${
                onglet === cle
                  ? 'border-codialis-mint font-semibold text-codialis-navy'
                  : 'border-transparent font-medium text-codialis-gray hover:text-codialis-navy'
              }`}
            >
              {libelle}
              {compte ? <span className="badge-warning ml-1 rounded px-1.5 py-0.5 text-[10px]">{compte}</span> : null}
            </button>
          ))}
        </div>

        <div {...attributsPanneau('panneau-devoirs', onglet)}>
          {liste.length === 0 ? (
            <p className="p-4 text-center text-sm text-codialis-gray sm:p-8">{VIDES[onglet]}</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {liste.map((devoir) => (
                <LigneDevoir
                  key={devoir.id}
                  devoir={devoir}
                  limites={data.limites}
                  ouvert={devoirOuvert === devoir.id}
                  onBasculer={() => setDevoirOuvert(devoirOuvert === devoir.id ? null : devoir.id)}
                  // Rendu, le devoir passe sous « Rendus » : il y reste ouvert.
                  onRendu={() => onglet === 'arendre' && ouvrir(devoir.id, 'rendus')}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
